import React, { useMemo } from "react";

import {
  DateRange as DateRangeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Box, IconButton, Modal, Theme, Typography } from "@mui/material";
import { format } from "date-fns";
import { camelCase, get, omit } from "lodash";
import { makeStyles } from "tss-react/mui";

import { DeleteIconButton, EditIconButton } from "@/components/IconButtons";
import HeightIcon from "@/components/Icons/HeightIcon";
import MenBodyMeasurement from "@/components/Icons/MenBodyMeasurement";
import WeightIcon from "@/components/Icons/WeightIcon";
import { graphql } from "@/gql";
import { BodyMeasurementListItem_SettingFragment, Gender } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { round } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import {
  getDevineIdealBodyWeight,
  LBS_MULTIPLIER,
} from "../utils/getIdealBodyWeight";

const useStyles = makeStyles()((theme: Theme) => ({
  calendarIconColor: {
    color: "#B8BEC9",
  },

  caption: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },

  fields: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: theme.spacing(3, 3),
    borderBottom: "1px solid #DEE6F0",
  },

  icon: {
    width: "100%",
    height: "100%",
  },

  infoContainer: {
    padding: theme.spacing(4, 4),
    width: "100%",
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #DEE6F0",
  },

  infoIcon: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#E8EEF8",
  },

  heading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: "bold",
  },

  mainInfoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    borderRight: "10px solid #DEE6F0",
  },

  modal: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    height: "85%",
    width: "100%",
    overflow: "hidden",
  },

  modalBody: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    width: "75%",
  },

  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2, 4),
  },

  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: theme.spacing(4, 4),
    borderBottom: "1px solid #DEE6F0",
  },

  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },

  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  scrollVertical: {
    overflowY: "scroll",
  },

  time: {
    fontSize: "16px",
    color: colors.gray,
    display: "block",
  },

  zeroPadding: {
    padding: 0,
  },
}));

type Props = {
  targetWeight?: number | null;
  settings?: BodyMeasurementListItem_SettingFragment | null;
  bm?: FragmentType<typeof BodyMeasurementDetailsModalFragment>;
  open: boolean;
  gender: Gender | undefined;
  latestHeightCm: number | undefined;
  latestWeightKg: number | undefined;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
};

const BodyMeasurementDetailsModalFragment = graphql(`
  fragment BodyMeasurementDetailsModal on BodyMeasurementsByDay {
    day
    bodyMeasurements {
      ...BodyMeasurementListItem
    }
  }
`);

const BodyMeasurementDetailsModal: React.FC<Props> = ({
  targetWeight,
  bm,
  open,
  onEdit,
  onClose,
  onDelete,
  settings,
  latestHeightCm,
  gender,
}) => {
  const { classes } = useStyles();
  const dateCreated = bm?.day
    ? format(new Date(bm?.day), "dd MMM, yyyy")
    : "N/A";
  const timeCreated = bm?.bodyMeasurements.createdAt
    ? format(new Date(bm.bodyMeasurements.createdAt), "hh:mm bb")
    : "-";
  const idealBodyWeight = round(
    getDevineIdealBodyWeight(latestHeightCm, gender) ?? 0,
    2
  );

  const displayFields = useMemo(
    () => () => {
      if (settings) {
        return settings.bodyMeasurementFields?.map((field, index) => {
          const key = camelCase(field.fieldName);
          const value = get(
            omit(bm?.bodyMeasurements, [
              "__typename",
              "images",
              "id",
              "time",
              "date",
            ]),
            key
          );

          if (field.fieldName === "weight_lbs") return;

          if (value !== null) {
            return (
              <Box key={index} className={classes.fields}>
                <Typography
                  className={classes.caption}
                  style={{ textTransform: "capitalize" }}
                >
                  {key.replace(/([a-z])([A-Z])/g, "$1 $2")}
                </Typography>
                <Typography className={classes.heading}>
                  {value != null ? value : "-"}
                </Typography>
              </Box>
            );
          } else if (field.visible) {
            return (
              <Box key={index} className={classes.fields}>
                <Typography
                  className={classes.caption}
                  style={{ textTransform: "capitalize" }}
                >
                  {key.replace(/([a-z])([A-Z])/g, "$1 $2")}
                </Typography>
                <Typography className={classes.heading}>
                  {value != null ? value : "-"}
                </Typography>
              </Box>
            );
          }
        });
      }
    },
    [settings, bm?.bodyMeasurements]
  );

  return (
    <Modal open={open} onClose={onClose} className={classes.root}>
      <Box className={classes.modal}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Body Measurement
            </Typography>
            <Box display="flex" alignItems="center">
              <EditIconButton onClick={onEdit} />
              <Box marginX={1} />
              <DeleteIconButton onClick={onDelete} />
              <Box marginX={1} />
              <IconButton
                onClick={onClose}
                className={classes.zeroPadding}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box width="100%" display="flex" overflow="auto">
            <Box className={classes.mainInfoContainer}>
              <Box className={classes.infoContainer}>
                <IconButton
                  color="default"
                  size="small"
                  disableRipple={true}
                  className={classes.infoIcon}
                >
                  <DateRangeIcon className={classes.calendarIconColor} />
                </IconButton>
                <Box padding={2} />
                <Box display="flex" alignItems="center">
                  <Typography className={classes.heading}>
                    {dateCreated}
                    <span className={classes.time}>{timeCreated} </span>
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.infoContainer}>
                <IconButton
                  color="default"
                  size="small"
                  disableRipple={true}
                  className={classes.infoIcon}
                >
                  <WeightIcon />
                </IconButton>
                <Box padding={2} />
                <Box>
                  <Typography className={classes.caption}>
                    Curent Weight
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography className={classes.heading}>
                      {`${bm?.bodyMeasurements.weightKg ?? "--"} kg`}
                    </Typography>
                    <Box padding={1} />
                    <Typography variant="caption" color="textSecondary">
                      {`(${round(
                        Number(bm?.bodyMeasurements.weightKg) * 2.20462,
                        1
                      ).toFixed(1)} lbs)`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className={classes.infoContainer}>
                <IconButton
                  color="default"
                  size="small"
                  disableRipple={true}
                  className={classes.infoIcon}
                >
                  <WeightIcon />
                </IconButton>
                <Box padding={2} />
                <Box>
                  <Typography className={classes.caption}>
                    Goal Weight
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography className={classes.heading}>
                      {`${parseFloat(
                        ((targetWeight || 0) * LBS_MULTIPLIER).toFixed(1)
                      )} lbs`}
                    </Typography>
                    <Box padding={1} />
                    <Typography variant="caption" color="textSecondary">
                      {`(${targetWeight} kg)`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className={classes.infoContainer}>
                <IconButton
                  color="default"
                  size="small"
                  disableRipple={true}
                  className={classes.infoIcon}
                >
                  <WeightIcon />
                </IconButton>
                <Box padding={2} />
                <Box>
                  <Typography className={classes.caption}>
                    Ideal Weight
                  </Typography>
                  <Typography className={classes.heading}>
                    {`${idealBodyWeight} kg`}
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.infoContainer}>
                <IconButton
                  color="default"
                  size="small"
                  disableRipple={true}
                  className={classes.infoIcon}
                >
                  <HeightIcon />
                </IconButton>
                <Box padding={2} />
                <Box>
                  <Typography className={classes.caption}>
                    Current Height
                  </Typography>
                  <Typography className={classes.heading}>
                    {`${bm?.bodyMeasurements.heightCm ?? 0} cm`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box flex={1} padding={5}>
              <MenBodyMeasurement className={classes.icon} />
            </Box>

            <Box flex={2} padding={2} className={classes.scrollVertical}>
              {displayFields()}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default BodyMeasurementDetailsModal;
